import React, {
  useState, useContext, useCallback, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useFormContext, useWatch } from 'react-hook-form';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import {
  HeadingBanner, Button,
} from 'anf-core-react';
import componentMap from './componentMap';
import skipStepFunctionMap from './skipStepFunctionMap';
import CheckoutPageContext from '../../context/CheckoutPageContext';
import { useStepsContext, StepsProvider } from './StepsContext';
import validate from './helpers/stepValidation';
import useNextButtonState from './hooks/useNextButtonState';
import ContinueButton from './ContinueButton';
import useCallbackFunctionMap from './hooks/useCallbackFunctionMap';
import { getNextStepById } from '../../tools/utils';
import useStepsSchema from './hooks/useStepsSchema';

function Steps({ schema }) {
  const {
    currentStep,
    setCurrentStep,
    clearStepError,
    completedSteps,
    setCompletedSteps,
    allStepsCompleted,
    setAllStepsCompleted,
  } = useStepsContext();
  const [isStepProcessing, setIsStepProcessing] = useState(false);
  const [transitioningSteps, setTransitioningSteps] = useState(new Set());
  // Tracks steps that were completed before entering edit, even if we temporarily
  // remove them from completedSteps to enforce re-validation.
  const [editedFromCompleted, setEditedFromCompleted] = useState(new Set());
  const stepRefs = useRef({});
  const { trigger, control } = useFormContext();
  const formValues = useWatch({ control });
  const callbackFuncionMap = useCallbackFunctionMap();
  const {
    checkoutPageState, updateCheckoutPageState,
    userExists,
  } = useContext(CheckoutPageContext);
  const isNextButtonEnabled = useNextButtonState(
    currentStep,
    schema.steps,
    componentMap,
    checkoutPageState,
  );

  const isSingleStep = schema.steps.length === 1;
  const showSummary = allStepsCompleted && !isSingleStep;

  // Initialize refs for each step
  schema.steps.forEach((step) => {
    if (!stepRefs.current[step.id]) {
      stepRefs.current[step.id] = React.createRef();
    }
    if (!stepRefs.current[`${step.id}-overview`]) {
      stepRefs.current[`${step.id}-overview`] = React.createRef();
    }
    if (!stepRefs.current[`${step.id}-heading`]) {
      stepRefs.current[`${step.id}-heading`] = React.createRef();
    }
  });

  // Scroll to step heading smoothly
  const scrollToStepHeading = (stepId) => {
    const headingRef = stepRefs.current[`${stepId}-heading`];
    if (headingRef?.current && typeof headingRef.current.scrollIntoView === 'function') {
      headingRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  };

  // Attempt to scroll to the first invalid field within a step's form content
  const scrollToFirstFieldError = (stepId) => {
    const contentRef = stepRefs.current[stepId];
    const container = contentRef?.current;
    if (container && typeof container.querySelector === 'function') {
      const firstInvalid = container.querySelector(
        '[aria-invalid="true"], .is-invalid, .error, [data-invalid="true"], [role="alert"]',
      );
      if (firstInvalid && typeof firstInvalid.scrollIntoView === 'function') {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        if (typeof firstInvalid.focus === 'function') firstInvalid.focus();
        return;
      }
    }
    // Fallback to the step heading if we fail to find an invalid field
    scrollToStepHeading(stepId);
  };

  const validateStepFields = useCallback(async (stepId) => (
    validate(stepId, schema.steps, componentMap, checkoutPageState, trigger, formValues)
  ), [schema.steps, checkoutPageState, trigger, formValues]);

  const handleNext = async () => {
    setIsStepProcessing(true);
    const isValid = await validateStepFields(currentStep);
    if (isValid) {
      const { onContinue = '' } = schema.steps.find((step) => step.id === currentStep);
      const callbackFn = callbackFuncionMap?.[onContinue];

      if (callbackFn) {
        const callbackFnResponse = await callbackFn();
        // If the callback function returns false, the step should not be marked as complete.
        if (!callbackFnResponse) {
          setIsStepProcessing(false);
          scrollToStepHeading(currentStep);
          return;
        }
      }

      // Start transition animation
      setTransitioningSteps((prev) => new Set([...prev, currentStep]));

      // Small delay to ensure smooth transition
      setTimeout(() => {
        clearStepError(currentStep);

        const nextStep = getNextStepById(schema.steps, currentStep);
        const shouldSkipNextStepFn = skipStepFunctionMap?.[nextStep?.skipStepAndShowOverview];
        const shouldSkipNextStep = shouldSkipNextStepFn?.(checkoutPageState);
        const stepToSkip = nextStep && shouldSkipNextStep ? [nextStep.id] : [];

        const newCompletedSteps = [...new Set([...completedSteps, currentStep, ...stepToSkip])];
        setCompletedSteps(newCompletedSteps);
        // Clear any previous "edited-from-completed" flag once the step is re-completed
        setEditedFromCompleted((prev) => {
          if (!prev?.size) return prev;
          const updated = new Set(prev);
          updated.delete(currentStep);
          return updated;
        });
        // End transition animation after a brief moment
        setTimeout(() => {
          setTransitioningSteps((prev) => {
            const updated = new Set(prev);
            updated.delete(currentStep);
            return updated;
          });

          // Scroll to the step heading after transition completes
          scrollToStepHeading(currentStep);
        }, 100);

        const nextIncompleteStep = schema.steps.find(
          (step) => !newCompletedSteps.includes(step.id),
        );
        if (nextIncompleteStep) {
          setCurrentStep(nextIncompleteStep.id);
        } else {
          setAllStepsCompleted(true);
          updateCheckoutPageState({ orderSubmit: null, currencyRepudiation: null });
        }
      }, 50); // Brief delay for transition timing
    } else {
      // On invalid state, scroll to the first errored field instead of setting a step-level error
      scrollToFirstFieldError(currentStep);
    }
    setIsStepProcessing(false);
  };

  const handleEdit = (stepId) => {
    setTransitioningSteps((prev) => new Set([...prev, stepId]));

    setTimeout(() => {
      const wasSummaryView = allStepsCompleted;
      setAllStepsCompleted(false);
      // If we're not coming from the summary view, mark steps as incomplete appropriately:
      // 1) The step we're leaving (currentStep) — user edited it but didn't press Continue.
      // 2) The step we're entering (stepId) — require explicit re-confirmation via Continue.
      if (!wasSummaryView) {
        if (currentStep && currentStep !== stepId) {
          setEditedFromCompleted((prev) => new Set([...prev, currentStep]));
          setCompletedSteps((prev) => prev.filter((id) => id !== currentStep));
        }
        setEditedFromCompleted((prev) => new Set([...prev, stepId]));
        setCompletedSteps((prev) => prev.filter((id) => id !== stepId));
      }
      setCurrentStep(stepId);
      clearStepError(stepId);

      // End transition animation
      setTimeout(() => {
        setTransitioningSteps((prev) => {
          const updated = new Set(prev);
          updated.delete(stepId);
          return updated;
        });

        // Scroll to the step heading after edit transition completes
        scrollToStepHeading(stepId);
      }, 100);
    }, 50);
  };

  const renderComponentsForStep = useCallback((step) => {
    if (!step.components || !Array.isArray(step.components)) return null;
    const wasCompleted = completedSteps.includes(step.id) || editedFromCompleted.has(step.id);
    return step.components.map((componentName) => {
      const StepComponent = componentMap[componentName];
      if (!StepComponent) throw new Error(`No component found for step component: ${componentName}`);
      return (
        <StepComponent
          key={`${step.id}-${componentName}`}
          stepId={step.id}
          title={step.title}
          wasStepCompleted={wasCompleted}
          {...step.props}
        />
      );
    });
  }, [completedSteps, editedFromCompleted]);

  const renderSummaryComponents = () => {
    if (!schema.summary?.components) return null;
    return schema.summary.components.map((componentName) => {
      const Component = componentMap[componentName];
      return Component ? <Component key={componentName} stepId={schema.steps[0]?.id} /> : null;
    });
  };

  const renderMobileRightRailForSteps = () => {
    const Component = componentMap.MobileCheckoutRightRailWrapper;
    return Component ? <Component key={`step-right-rail-${Component.name}`} stepId={currentStep} /> : null;
  };

  const renderFooter = () => {
    const Component = componentMap.FooterRightRailWrapper;
    return Component ? (
      <Component
        key={`footer-right-rail-${Component.name}`}
        stepId={currentStep}
      />
    ) : null;
  };

  const renderAlwaysDisplay = () => {
    if (!schema.alwaysDisplay?.components) return null;
    return schema.alwaysDisplay.components.map((componentName) => {
      const Component = componentMap[componentName];
      return Component ? <Component key={`always-${componentName}`} stepId={currentStep} /> : null;
    });
  };

  const renderOverviewForStep = (stepId) => {
    const step = schema.steps.find((s) => s.id === stepId);
    if (!step?.overviewComponent) return null;
    const OverviewComponent = componentMap[step.overviewComponent];
    return OverviewComponent ? <OverviewComponent key={`${step.id}-overview`} stepId={stepId} /> : null;
  };

  const renderStepContent = (step) => {
    const isCurrent = step.id === currentStep;
    const isCompleted = completedSteps.includes(step.id);
    const isTransitioning = transitioningSteps.has(step.id);

    // Determine what content to show
    const showForm = isCurrent && !isTransitioning;
    const showOverview = isCompleted && !isCurrent && !isTransitioning;
    const showFormWithFooter = isCurrent && !isTransitioning;

    return (
      <div className="step-content-container" data-testid={`${step.testId}-content-container`}>
        <TransitionGroup>
          {showForm && (
            <CSSTransition
              key={`${step.id}-form`}
              nodeRef={stepRefs.current[step.id]}
              timeout={300}
              classNames="step-form"
              appear
            >
              <div ref={stepRefs.current[step.id]} className="step-content-wrapper" data-testid={`${step.testId}-content-wrapper`}>
                <div className="step-content" data-testid={`${step.testId}-content`}>{renderComponentsForStep(step)}</div>
              </div>
            </CSSTransition>
          )}

          {showOverview && (
            <CSSTransition
              key={`${step.id}-overview`}
              nodeRef={stepRefs.current[`${step.id}-overview`]}
              timeout={300}
              classNames="step-overview"
              appear
            >
              <div ref={stepRefs.current[`${step.id}-overview`]} className="step-content-wrapper">
                <div className="step-content" data-testid={`${step.testId}-content`}>{renderOverviewForStep(step.id)}</div>
              </div>
            </CSSTransition>
          )}
        </TransitionGroup>

        {showFormWithFooter && (
          <div className="step-footer" data-testid={`${step.testId}-footer`}>
            <div className="step-actions">
              {isSingleStep ? (
                schema.summary?.submitButton && React.createElement(
                  componentMap[schema.summary.submitButton],
                  { stepId: currentStep, 'data-testid': 'submit-button' },
                )
              ) : (
                <ContinueButton
                  onContinue={handleNext}
                  step={step}
                  isEditing={isCurrent && (isCompleted || editedFromCompleted.has(step.id))}
                  stepId={currentStep}
                  isEnabled={isNextButtonEnabled}
                  isStepProcessing={isStepProcessing}
                  userExists={userExists}
                />
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="steps-component" className="steps-component">
      {showSummary ? (
        <>
          {schema.steps.map((step) => {
            const skipStepAndShowOverviewFn = skipStepFunctionMap?.[step.skipStepAndShowOverview];
            const skipStepAndShowOverview = skipStepAndShowOverviewFn?.(checkoutPageState);
            return (
              <div key={step.id} className="step-wrapper completed">
                <div ref={stepRefs.current[`${step.id}-heading`]}>
                  <HeadingBanner variant="boxed">
                    <div className="heading-banner-content" data-testid={`${step.testId}-header`}>
                      <h3>{step.title?.value || step.name}</h3>
                      {!skipStepAndShowOverview && (
                      <Button variant="borderless" onClick={() => handleEdit(step.id)}>Edit</Button>)}
                    </div>
                  </HeadingBanner>
                </div>
                <div className="step-content-wrapper">
                  <div className="step-content" data-testid={`${step.testId}-content`}>{renderOverviewForStep(step.id)}</div>
                </div>
              </div>
            );
          })}
          <div className="always-display">{renderAlwaysDisplay()}</div>
          <div className="summary-content">{renderSummaryComponents()}</div>
          {schema.summary?.submitButton
            && componentMap[schema.summary.submitButton] && (
              <div className="submit-button-container">
                {React.createElement(
                  componentMap[schema.summary.submitButton],
                  { stepId: schema.steps[0]?.id },
                )}
              </div>
          )}
          <div className="step-footer" data-testid="step-footer">{renderFooter()}</div>
        </>
      ) : (
        <>
          {schema.steps.map((s) => {
            const isCurrent = s.id === currentStep;
            const isCompleted = completedSteps.includes(s.id);
            const skipStepAndShowOverviewFn = skipStepFunctionMap?.[s.skipStepAndShowOverview];
            const skipStepAndShowOverview = skipStepAndShowOverviewFn?.(checkoutPageState);
            return (
              <div key={s.id} data-testid={`${s.testId}-step-wrapper`} className={`step-wrapper ${isCurrent ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                {s.title && (
                  <div ref={stepRefs.current[`${s.id}-heading`]}>
                    <HeadingBanner variant="boxed">
                      <div className="heading-banner-content" data-testid={`${s.testId}-header`}>
                        <h3>{s.title.value}</h3>
                        {isCompleted && !isCurrent && !skipStepAndShowOverview && (
                          <Button variant="borderless" onClick={() => handleEdit(s.id)}>{s.editButtonLabel?.value}</Button>
                        )}
                      </div>
                    </HeadingBanner>
                  </div>
                )}
                {renderStepContent(s)}
              </div>
            );
          })}
          <div className="always-display">{renderAlwaysDisplay()}</div>
          <div className="mobile-right-rail-steps">{renderMobileRightRailForSteps()}</div>
          <div className="step-footer" data-testid="step-footer">{renderFooter()}</div>
        </>
      )}
    </div>
  );
}

function StepsWrapper(props) {
  const stepsSchema = useStepsSchema();

  return (
    <StepsProvider {...props} schema={stepsSchema}>
      <Steps {...props} schema={stepsSchema} />
    </StepsProvider>
  );
}

Steps.propTypes = {
  schema: PropTypes.shape({
    steps: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.shape({
          value: PropTypes.string,
        }),
        components: PropTypes.arrayOf(PropTypes.string).isRequired,
        required: PropTypes.bool,
        errorMessage: PropTypes.shape({
          value: PropTypes.string,
        }),
        overviewComponent: PropTypes.string,
        submitButton: PropTypes.string,
      }),
    ).isRequired,
    summary: PropTypes.shape({
      components: PropTypes.arrayOf(PropTypes.string),
      submitButton: PropTypes.string,
    }),
    alwaysDisplay: PropTypes.shape({
      components: PropTypes.arrayOf(PropTypes.string),
    }),
  }).isRequired,
  initialStep: PropTypes.number,
};

Steps.defaultProps = {
  initialStep: 0,
};

export default StepsWrapper;
